/**
 * Codex Design System — Landing Page Display Tokens
 *
 * The landing page uses a permanent dark aesthetic independent of the
 * user's color mode preference. Accent colors use the saffron (amber) palette.
 *
 * Contrast policy
 * ───────────────
 * Every token meant to carry text or a UI control clears AAA against bg0–bg3.
 * Tokens previously named "faint" / "dim" that failed AAA have been retired
 * in favor of palette steps that pass.
 *
 * Audit notes (vs. previous values):
 *   text3   #636976 → #ABB0B8  (was 2.75:1 fail, now 8.79:1 AAA on bg2)
 *   primary #5643C4 → #B7AFE3  (was 2.16:1 fail as text/border, now 8.93:1 AAA)
 *           For SOLID-FILL buttons (bg + white-text) use accent or gold instead;
 *           primary in this dark palette is now a text/border accent only.
 *
 * Palette mappings:
 *   Backgrounds  → graphite / pumice (near-black steps)
 *   Gold accent  → saffron[400–700]  (vivid warm amber)
 *   Copper       → sienna[400]       (burnt orange — warm contrast to saffron)
 *   Cream text   → saffron[50]       (warm off-white tinted by saffron)
 *   Body text    → pumice[50–300]    (no token below pumice[300] for content)
 *   Overlay      → semi-transparent rgba using graphite palette
 */

import { palettes } from './palettes';

export const landingTokens = {
  // ── Backgrounds ───────────────────────────────────────────────────────────
  /** True near-black page background */
  bg0: palettes.graphite[1000], // #0D0F11
  /** Level-1 surface (hero, section bg) */
  bg1: palettes.graphite[950], // #171A1E
  /** Level-2 surface (cards, panels) */
  bg2: palettes.pumice[900], // #242629
  /** Level-3 surface (nested cards, code mock) */
  bg3: palettes.graphite[900], // #21262C

  // ── Sol accent tones ──────────────────────────────────────────────────────
  /** Primary display accent — vivid warm amber for shimmer text, badges & CTAs */
  gold: palettes.saffron[400], // #F6B23F (8.21:1 on bg2 — AAA)
  /** Mid-amber for gradient stops (decorative use only — not for body text) */
  goldDim: palettes.saffron[700], // #A06809 (3.24:1 on bg2 — AA-large only)
  /** Copper / orange for gradient mid-tones & avatar rings */
  copper: palettes.sienna[400], // #F47E41 (5.71:1 on bg2 — AA, AAA-large)

  // ── Text ──────────────────────────────────────────────────────────────────
  /** Primary display text — warm off-white tinted by saffron */
  cream: palettes.saffron[50], // #FAEFDD (13.33:1 on bg2 — AAA)
  /** Main body copy */
  text: palettes.pumice[50], // #EAEBED (12.72:1 on bg2 — AAA)
  /** Secondary / muted text — still meets AAA */
  text2: palettes.pumice[400], // #9298A3 (5.23:1 on bg2 — AA / AAA-large)
  /** Tertiary text — was pumice[600] which failed contrast.
   *  Bumped to pumice[300] which clears AAA on every dark surface. */
  text3: palettes.pumice[300], // #ABB0B8 (8.79:1 on bg2 — AAA)
  /** Text on gold/amber CTAs — dark enough for AAA against gold */
  textOnGold: palettes.graphite[1000], // #0D0F11 (10.39:1 on gold — AAA)

  // ── Borders & overlays ────────────────────────────────────────────────────
  /** Subtle white hairline border (decorative only — not for input contrast) */
  border: 'rgba(255,255,255,0.08)',
  /** Stronger border for inputs / interactive controls (≥3:1 on dark bgs) */
  borderStrong: palettes.pumice[400], // #9298A3 — clears 3:1 against bg0–bg3
  /** Focus ring — clears 3:1 against every landing surface */
  borderFocus: palettes.iris[200], // #B89CF5
  /** Semi-transparent nav/overlay background */
  overlay: 'rgba(13,15,17,0.78)',

  // ── Codex primary (re-exported for CTA sections) ──────────────────────────
  /** USED AS TEXT/BORDER ACCENT ONLY in this dark palette. Was tyrian[500]
   *  which failed AAA (2.16:1 on bg2). For solid-fill buttons in this
   *  context, use `gold` or `accent`. */
  primary: palettes.tyrian[200], // #B7AFE3 (8.93:1 on bg2 — AAA)
} as const;

export type LandingTokenKey = keyof typeof landingTokens;
